/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import { useEffect, useRef, useMemo } from "react";
import { ROUTES, WIDGET_CALLBACK_URL } from "../constants/constants";
import usePluginUtils from "./usePluginUtils";
import { useToast } from "../components/ui/Toast";
import { useSelector } from "react-redux";
import { FlowType } from "../store/slices/flowSlice";

export const useRedirectRoutes = () => {
  const { isPluginEnabled } = usePluginUtils();
  const { toast } = useToast();
  const toastShownRef = useRef(false);

  const clientLoginEnabled = isPluginEnabled("client_login");
  const termsAndConditionsEnabled = isPluginEnabled("user_license");
  const activeFlow = useSelector(state => state.flow.activeFlow);

  useEffect(() => {
    if (
      window.location.hash.includes(ROUTES.CLIENT_RESTORE_PASSWORD) &&
      !clientLoginEnabled &&
      !toastShownRef.current
    ) {
      const timeoutId = setTimeout(() => {
        toast({
          title: "Error",
          description: "Password reset functionality is not available",
          variant: "error",
        });
        toastShownRef.current = true;
      }, 0);

      return () => clearTimeout(timeoutId);
    }
  }, [toast, clientLoginEnabled]);

  return useMemo(() => {
    if (!window.location.href.includes(WIDGET_CALLBACK_URL)) return null;

    const rescheduleMatch = window.location.hash.match(/\/client\/reschedule-booking\/id\/([^/]+)/);
    
    if (rescheduleMatch) {
      return ROUTES.RESCHEDULE_BOOKING.replace(":id", rescheduleMatch[1]);
    }

    const invoicePaymentRoute = activeFlow === FlowType.CART
      ? ROUTES.CART_ITEM_STATUS
      : ROUTES.BOOKING_STATUS;

    const redirectUrls = {
      "/ext/invoice-payment/": invoicePaymentRoute,
      "/client/bookings": ROUTES.CLIENT_BOOKINGS,
      "/client/cancel-booking": ROUTES.CANCEL_BOOKING,
      "/client/pay-later": ROUTES.INVOICE_PAY,
      "/client/restore-password": clientLoginEnabled
        ? ROUTES.CLIENT_RESTORE_PASSWORD
        : null,
      "/client/gdpr-report": ROUTES.PERSONAL_DATA_REPORT,
      "/client/terms-and-conditions": termsAndConditionsEnabled
        ? ROUTES.TERMS_AND_CONDITIONS
        : null,
      "/online-meeting/view/": ROUTES.ONLINE_MEETING,
    };

    const matchedRoute = Object.keys(redirectUrls).find((key) =>
      window.location.hash.includes(key)
    );

    return matchedRoute ? redirectUrls[matchedRoute] : null;
  }, [clientLoginEnabled, termsAndConditionsEnabled, activeFlow]);
};
